import {
  HideDrawerFrontComponent,
  PlannerCoreApi,
  ProductType
} from '@inter-ikea-storageone/range';
import { ComponentIds, Entity } from '@inter-ikea-core/core';
import { DraggableProduct } from 'products/interfaces/DraggableProduct';
import { useCallback, useEffect } from 'react';

function useDragClipping(
  coreApi: PlannerCoreApi,
  canvas: React.RefObject<HTMLCanvasElement>,
  draggedItem?: DraggableProduct
) {
  const handleDrag = useCallback(
    (event: DragEvent) => {
      if (!canvas.current || !draggedItem || !isClippingProduct(draggedItem)) {
        return;
      }

      updateClippingOnDrawers(getEntityAtMouseCursor(canvas.current, event));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [draggedItem]
  );

  const getEntityAtMouseCursor = (
    currentCanvas: HTMLCanvasElement,
    event: DragEvent
  ): Entity | undefined => {
    const canvasBoundingRect = currentCanvas.getBoundingClientRect();
    const x = event.x - canvasBoundingRect.left;
    const y = event.y - canvasBoundingRect.top;

    const drawers = coreApi.entities.getByComponent(ComponentIds.DRAWER_COMPONENT_ID);
    const drawersObjects = coreApi.entities.getObjectIds(drawers);

    const raycastedPosition = coreApi.ipexViewer.raycast_object_using_canvas_coordinate(
      x,
      y,
      drawersObjects
    );

    const foundEntity = coreApi.entities.findEntityWithObjectId(raycastedPosition?.object);

    return foundEntity;
  };

  const updateClippingOnDrawers = (foundEntity?: Entity) => {
    coreApi.entities.getByComponent(HideDrawerFrontComponent.id).forEach(clippedComponent => {
      if (clippedComponent.id !== foundEntity?.id) {
        clippedComponent.removeComponent(HideDrawerFrontComponent.id);
      }
    });

    foundEntity &&
      !foundEntity.hasComponent(HideDrawerFrontComponent.id) &&
      foundEntity.addComponent(new HideDrawerFrontComponent());
  };

  const isClippingProduct = (draggedItem: DraggableProduct): boolean => {
    const productType = draggedItem.targets[0]?.type;

    return productType
      ? ([ProductType.PAX_DRAWER_MAT, ProductType.PAX_DRAWER_BOX_ASM] as string[]).includes(
          productType
        )
      : false;
  };

  useEffect(() => {
    document.addEventListener('drag', handleDrag, false);
    return () => {
      document.removeEventListener('drag', handleDrag, false);
    };
  }, [handleDrag]);
}

export default useDragClipping;
